import { defineComponent as _defineComponent } from 'vue'
import {
  inject,
  nextTick,
  onDeactivated,
  PropType,
  provide,
  ref,
  watch,
} from "vue";
import InlineSvg from "vue-inline-svg";
import { IBusStop } from "@/domain/entities/busStop";
import { IFavoriteLine } from "@/domain/entities/favoriteLine";
import CheckboxInput from "@/components/shared/inputs/CheckboxInput.vue";
import {
  BookIntervalCode,
  ITurnBusLastSeatCount,
  PROVIDE_KEY_IS_BUS_RUNNING,
  PROVIDE_KEY_SHOW_ERROR_MODAL,
  PROVIDE_KEY_SHOW_MONTHLY_BOOK_DETAIL,
  PROVIDE_KEY_TURN_BUSES_REMAIN_SEAT_COUNT,
  ShowErrorModal,
} from "@/types";
import { computed, ComputedRef, Ref } from "@vue/reactivity";
import useGetLineRouteName from "@/use/useGetLineRouteName";
import { useGetBookBeginDateTimeStr } from "@/use/useGetBookIntervalStr";
import LineTypeChip from "@/components/shared/chip/LineTypeChip.vue";
import Tooltip from "@/components/shared/Tooltip.vue";
import BookDetail from "@/views/book/BookDetail.vue";
import BookChip from "../shared/chip/BookChip.vue";
import MonthlyBookSeatAndPayment from "../book/MonthlyBookSeatAndPayment.vue";
import { IBookableDatesInfo } from "@/domain/entities/book";
import MonthlyBookDetailWithDuplicatedSeat from "@/views/book/MonthlyBookDetailWithDuplicatedSeat.vue";
import { IOnBoardLine } from "@/domain/entities/onBoardLine";
import { useServiceStore } from "@/stores/serviceStore";
import { useUserStore } from "@/stores/userStore";
import PageModal from "../shared/modal/base/PageModal.vue";


const __default__ = {
  name: "MainCardFront",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  favoriteLine: {
    type: Object as PropType<IFavoriteLine>,
    required: true,
  },
  cardIndex: {
    type: Number,
    required: true,
  },
  currentCardIndex: {
    type: Number,
    required: true,
  },
  onBoardLine: {
    type: Object as PropType<IOnBoardLine>,
  },
  isInsideShuttle: {
    type: Boolean,
    default: false,
  },
},
  emits: ["onClickLineInfo", "onClickBusStopLocation", "onChangedCheck", "onClickReportBtn"],
  setup(__props, { expose: __expose, emit: __emit }) {

const props = __props;

const emit = __emit;

const showErrorModal = inject(PROVIDE_KEY_SHOW_ERROR_MODAL) as ShowErrorModal;

const serviceStore = useServiceStore();
const userStore = useUserStore();

const getOnBusStopName = computed(() => {
  if (!props.favoriteLine.getOnBusStop) return "-";

  return `${props.favoriteLine.getOnBusStop.name} (${props.favoriteLine.getOnBusStop.departureTime})`;
});

const getOffBusStopName = computed(() => {
  if (!props.favoriteLine.getOffBusStop) return "-";

  return `${props.favoriteLine.getOffBusStop.name} (${props.favoriteLine.getOffBusStop.arrivalTime})`;
});

/** 버스 운행중 여부 */
const isBusRunning = inject(PROVIDE_KEY_IS_BUS_RUNNING) as Ref<boolean>;

/** 버스들의 잔여좌석 리스트 */
const turnBusLastSeatCountList = inject(
  PROVIDE_KEY_TURN_BUSES_REMAIN_SEAT_COUNT
) as ComputedRef<ITurnBusLastSeatCount[]>;

const displayLastSeatCount = (allocUid: string) => {
  if (!isBusRunning.value) return "";
  if (
    turnBusLastSeatCountList.value &&
    turnBusLastSeatCountList.value.length === 0
  ) {
    return "";
  }

  const selectedBusRemainSeatCount = turnBusLastSeatCountList.value.find(
    (seatCount) => seatCount.allocUid === allocUid
  );

  if (!selectedBusRemainSeatCount) {
    return "";
  }

  const { remainSeatCount, boardingCount } = selectedBusRemainSeatCount;

  if (props.isInsideShuttle) {
    return boardingCount === -1 ? "" : `(탑승${boardingCount})`;
  }

  return remainSeatCount === -1 ? "" : `(잔여${remainSeatCount})`;
};

/** 예약제한 상태 여부 */
const isBookBlocked = computed(() => userStore.isBookBlocked);

const shouldShowRemainBookCount = computed(
  () => !!serviceStore.serviceInfo?.shouldShowRemainBookCount
);

const userRemainBookCount = computed(() => userStore.remainBookCount ?? "-");

const isBookCountExceeded = computed(
  () => shouldShowRemainBookCount.value && (userStore.remainBookCount ?? 0) <= 0
);

const isBoarding = computed(() => props.favoriteLine.isBoarding);

const dailyBookInterval = computed(() => {
  return props.favoriteLine.bookIntervals?.find(
    (bookInterval) => bookInterval.bookIntervalCode === BookIntervalCode.DAY
  );
});

const monthlyBookInterval = computed(() => {
  return props.favoriteLine.bookIntervals?.find(
    (bookInterval) => bookInterval.bookIntervalCode === BookIntervalCode.MONTH
  );
});

const monthlyBookable = computed(
  () =>
    !!monthlyBookInterval && (props.favoriteLine.lineFare.monthlyFare ?? 0) > 0
);

const isBookable = computed(
  () => dailyBookInterval.value || monthlyBookInterval.value
);

const isSameBookBeginDateTime = computed(() => {
  return (
    useGetBookBeginDateTimeStr(dailyBookInterval.value) ===
    useGetBookBeginDateTimeStr(monthlyBookInterval.value)
  );
});

const monthlyBookLabel = computed(() => {
  return isSameBookBeginDateTime
    ? "월예약"
    : `월예약(${useGetBookBeginDateTimeStr(monthlyBookInterval.value)} 오픈)`;
});

const driveWeekDayText = computed(
  () =>
    props.favoriteLine.earlyDriveInfos
      ?.map((earlyDriveInfo) => earlyDriveInfo.weekdayName)
      ?.join(", ") ?? "-"
);

const hasBusPlateNumber = computed(() =>
  props.favoriteLine.buses?.some((bus) => !!bus.carNo)
);

const busPlateNumbers = computed(() => {
  if (props.favoriteLine.boardedCarNo) {
    return [props.favoriteLine.boardedCarNo];
  } else {
    return props.favoriteLine.buses
      ?.filter((bus) => bus.carNo)
      .map((bus) => bus.carNo);
  }
});

const busOrderCount = computed(() => {
  return props.favoriteLine.buses?.length ?? 0;
});

const busPlateNumberText = computed(() => {
  if (props.favoriteLine.boardedCarNo) {
    return props.favoriteLine.boardedCarNo;
  }

  const bus = props.favoriteLine.buses?.find((bus) => !!bus.carNo?.length);

  if (!bus)
    return ` - ${
      busOrderCount.value > 1 ? `외 ${busOrderCount.value - 1}` : ""
    }`;

  if (props.favoriteLine.isBoarding) {
    return props.onBoardLine?.carNo ?? "-";
  }

  const selectedBusLastSeatCount = turnBusLastSeatCountList.value.find(
    (seatCount) => seatCount.allocUid === bus?.allocUid
  );

  const singleBusText = `${bus.carNo}`;
  const multipleBusText = `${bus.carNo} 외 ${busOrderCount.value - 1}`;

  const isSingleBus = busOrderCount.value === 1;
  const busPlateText = isSingleBus ? singleBusText : multipleBusText;

  if (!selectedBusLastSeatCount || !isBusRunning.value) {
    return busPlateText;
  }

  const { remainSeatCount, boardingCount } = selectedBusLastSeatCount;

  if (props.isInsideShuttle) {
    const boardingCountText =
      boardingCount === -1 ? "" : `(탑승${boardingCount})`;

    return isSingleBus ? `${busPlateText}${boardingCountText}` : busPlateText;
  }

  const remainSeatCountText =
    remainSeatCount === -1 ? "" : `(잔여${remainSeatCount})`;

  return isSingleBus ? `${busPlateText}${remainSeatCountText}` : busPlateText;
});

const emitOnClickBusStopLocation = (busStop?: IBusStop) => {
  if (!busStop) return;

  closeTooltip();
  emit("onClickBusStopLocation", busStop);
};

// 차량번호 Tooltip 처리
const shouldShowCarNoTooltip = ref(false);
const carNoTooltipTargetElement = ref<HTMLElement>();

const toogleShowCarNoTooltipIfNeeded = (event: Event) => {
  if (busOrderCount.value <= 1) return;
  shouldShowCarNoTooltip.value = !shouldShowCarNoTooltip.value;
  if (shouldShowCarNoTooltip.value) {
    carNoTooltipTargetElement.value = event.target as HTMLElement;
  }
};

const closeBusPlateNumbersTooltip = () => {
  shouldShowCarNoTooltip.value = false;
};

// 월예약 안내 Tooltip 처리
const shouldShowMonthlyBookTooltip = ref(false);
const monthlyBookTooltipTargetElement = ref<HTMLElement>();
let timeout: ReturnType<typeof setTimeout>;

const toogleShowMonthlyBookTooltip = (event: Event) => {
  if (!shouldShowMonthlyBookTooltip.value) {
    showTooltip();
    monthlyBookTooltipTargetElement.value = event.target as HTMLElement;
  } else {
    hideTooltipImmediately();
  }
};

const showTooltip = () => {
  shouldShowMonthlyBookTooltip.value = true;
  hideTooltipAfterMillis(3000);
};

const hideTooltipAfterMillis = (millis: number) => {
  timeout = setTimeout(() => {
    shouldShowMonthlyBookTooltip.value = false;
  }, millis);
};

const hideTooltipImmediately = () => {
  if (timeout) clearTimeout(timeout);
  shouldShowMonthlyBookTooltip.value = false;
};

// 차량 번호, 월예약 안내 Tooltip 창 닫기
const closeTooltip = () => {
  hideTooltipImmediately();
  closeBusPlateNumbersTooltip();
};

const emitOnChangedCheck = (checked: boolean) => {
  checked ? showTooltip() : hideTooltipImmediately();
  emit("onChangedCheck", checked);
};

const emitOnClickReportBtn = () => {
  closeTooltip();
  emit("onClickReportBtn");
};

const handleError = (title: string, errorMessage?: string, result?: any) => {
  showErrorModal(
    title,
    errorMessage ?? result?.errorMessage,
    result?.resultCode,
    result?.errorTime
  );
};

/** 각종 버튼들 눌렀을때의 처리 */
// 예약하기 버튼 처리
const shouldShowBookDetail = ref(false);
const toggleShowBookDetail = (show: boolean) => {
  shouldShowBookDetail.value = show;
};

// 월예약 달력 화면 처리
const shouldShowMonthlyBookDetail = ref(false);
const toggleShowMonthlyBookDetail = (show: boolean) => {
  shouldShowMonthlyBookDetail.value = show;
};

const bookableDatesInfos = ref<IBookableDatesInfo[]>([]);
const showMonthlyBookDetail = (datesInfos: IBookableDatesInfo[]) => {
  shouldShowMonthlySeatAndPayment.value = false;
  bookableDatesInfos.value = datesInfos;
  toggleShowMonthlyBookDetail(true);
};
provide(PROVIDE_KEY_SHOW_MONTHLY_BOOK_DETAIL, showMonthlyBookDetail);

// 예약버튼 클릭 시
const monthlyBookSeatAndPayment = ref<typeof MonthlyBookSeatAndPayment>();
const shouldShowMonthlySeatAndPayment = ref(false);
const onClickBook = () => {
  if (isBookBlocked.value || isBookCountExceeded.value) return;

  if (props.favoriteLine.monthlyBookChecked) {
    // 월예약 처리
    shouldShowMonthlySeatAndPayment.value = true;
    nextTick(() => {
      monthlyBookSeatAndPayment.value?.proceedMonthlyBook();
    });
  } else {
    // 일예약 처리
    toggleShowBookDetail(true);
  }
};

// TODO 빠른 예약일과 잔여 좌석 수 사용시 아래 코드 활성화
/** 빠른 예약일 및 잔여 좌석수 가져오기 */
// const earliestBookableDate = ref();
// const earliestDateSeatsCount = ref();
// const getEarliestBookableDate = async (
//   turnId: string,
//   getOnBusStopId: string,
//   getOffBusStopId: string
// ) => {
//   const earliestBookableDateResult = await bookStore.getEarliestBookableDate(
//     turnId,
//     getOnBusStopId,
//     getOffBusStopId,
//     BookIntervalCode.DAY,
//     true
//   );

//   earliestBookableDate.value =
//     earliestBookableDateResult?.bookableDates?.[0]?.date;
//   earliestDateSeatsCount.value =
//     earliestBookableDateResult?.bookableDates?.[0].remainingSeatsCount;
// };

onDeactivated(() => {
  closeTooltip();
});

watch(
  () => [props.cardIndex, props.currentCardIndex],
  ([cardIndex, currentCardIndex]) => {
    if (cardIndex !== currentCardIndex) return;

    const turnId = props.favoriteLine.turnId;
    // TODO 빠른 예약일과 잔여 좌석 수 사용시 아래 코드 활성화
    // const getOnBusStopId = props.favoriteLine.getOnBusStop.id;
    // const getOffBusStopId = props.favoriteLine.getOffBusStop.id;
    // getEarliestBookableDate(turnId, getOnBusStopId, getOffBusStopId);
  },
  { immediate: true }
);

__expose({ closeTooltip });

const __returned__ = { props, emit, showErrorModal, serviceStore, userStore, getOnBusStopName, getOffBusStopName, isBusRunning, turnBusLastSeatCountList, displayLastSeatCount, isBookBlocked, shouldShowRemainBookCount, userRemainBookCount, isBookCountExceeded, isBoarding, dailyBookInterval, monthlyBookInterval, monthlyBookable, isBookable, isSameBookBeginDateTime, monthlyBookLabel, driveWeekDayText, hasBusPlateNumber, busPlateNumbers, busOrderCount, busPlateNumberText, emitOnClickBusStopLocation, shouldShowCarNoTooltip, carNoTooltipTargetElement, toogleShowCarNoTooltipIfNeeded, closeBusPlateNumbersTooltip, shouldShowMonthlyBookTooltip, monthlyBookTooltipTargetElement, get timeout() { return timeout }, set timeout(v) { timeout = v }, toogleShowMonthlyBookTooltip, showTooltip, hideTooltipAfterMillis, hideTooltipImmediately, closeTooltip, emitOnChangedCheck, emitOnClickReportBtn, handleError, shouldShowBookDetail, toggleShowBookDetail, shouldShowMonthlyBookDetail, toggleShowMonthlyBookDetail, bookableDatesInfos, showMonthlyBookDetail, monthlyBookSeatAndPayment, shouldShowMonthlySeatAndPayment, onClickBook, get InlineSvg() { return InlineSvg }, CheckboxInput, get BookIntervalCode() { return BookIntervalCode }, get useGetLineRouteName() { return useGetLineRouteName }, LineTypeChip, Tooltip, BookDetail, BookChip, MonthlyBookSeatAndPayment, MonthlyBookDetailWithDuplicatedSeat, PageModal }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})